import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useForm } from 'react-hook-form';
import { X, Save, Loader2, Calendar } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import postService from '../../services/postService';
import { Post } from '../../types';

interface PostEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  post: Post & { platforms?: string[] };
  onSuccess?: () => void;
}

interface PostEditFormData {
  title: string;
  content: string;
  status: 'draft' | 'scheduled' | 'published';
  scheduled_at?: string;
}

const PostEditModal: React.FC<PostEditModalProps> = ({
  isOpen,
  onClose,
  post,
  onSuccess
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isDirty }
  } = useForm<PostEditFormData>();

  const watchedStatus = watch('status');

  // Initialize form with post data
  useEffect(() => {
    if (post && isOpen) {
      setValue('title', post.title || '');
      setValue('content', post.content || '');
      setValue('status', post.status);
      
      if (post.scheduled_at) {
        // Convert UTC datetime to local datetime-local format
        const localDate = new Date(post.scheduled_at);
        const localDateString = localDate.toISOString().slice(0, 16);
        setValue('scheduled_at', localDateString);
      }

      console.log('PostEditModal: Initialized with post data:', {
        id: post.id,
        title: post.title,
        content: post.content,
        contentLength: post.content?.length || 0,
        status: post.status,
        platforms: post.platforms
      });
    }
  }, [post, isOpen, setValue]);

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.pointerEvents = 'none';
      return () => {
        document.body.style.overflow = '';
        document.body.style.pointerEvents = '';
      };
    }
  }, [isOpen]);

  const onSubmit = async (data: PostEditFormData) => {
    setIsLoading(true);

    try {
      // Convert local datetime to UTC for scheduled_at
      let scheduledAt = data.scheduled_at;
      if (scheduledAt && data.status === 'scheduled') {
        const localDate = new Date(scheduledAt);
        scheduledAt = localDate.toISOString();
      }

      const updateData = {
        title: data.title,
        content: data.content,
        status: data.status,
        scheduled_at: data.status === 'scheduled' ? scheduledAt : undefined
      };

      console.log('PostEditModal: Updating post with data:', {
        postId: post.id,
        updateData,
        contentLength: data.content?.length || 0
      });

      const updatedPost = await postService.updatePost(post.id, updateData);

      console.log('PostEditModal: Post updated successfully:', {
        id: updatedPost.id,
        title: updatedPost.title,
        content: updatedPost.content,
        contentLength: updatedPost.content?.length || 0,
        status: updatedPost.status
      });

      toast({
        title: "Post Updated",
        description: "Your post has been updated successfully.",
      });

      if (onSuccess) {
        onSuccess();
      }
      
      onClose();
    } catch (error: any) {
      let errorMessage = 'Failed to update post';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          errorMessage = detail.map(e => e.msg).join(', ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast({
        title: "Update Failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4"
      style={{ zIndex: 99999 }}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
        style={{ pointerEvents: 'auto' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-xl font-semibold text-black dark:text-white">
              Edit Post
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Platform: {post.platforms?.join(', ') || 'Unknown'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="overflow-y-auto max-h-[calc(90vh-140px)] p-6 space-y-6">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-black dark:text-gray-100 mb-2">
                Title
              </label>
              <input
                {...register('title', {
                  required: 'Title is required',
                  maxLength: { value: 200, message: 'Title must be less than 200 characters' }
                })}
                type="text"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-black dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                placeholder="Enter post title..."
              />
              {errors.title && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.title.message}</p>
              )}
            </div>

            {/* Content */}
            <div>
              <label className="block text-sm font-medium text-black dark:text-gray-100 mb-2">
                Content
              </label>
              <textarea
                {...register('content', {
                  required: 'Content is required',
                  maxLength: { value: 2000, message: 'Content must be less than 2000 characters' }
                })}
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-black dark:text-white placeholder-gray-500 dark:placeholder-gray-400 resize-none"
                placeholder="Write your post content..."
              />
              <div className="flex justify-between items-center mt-2">
                {errors.content && (
                  <p className="text-sm text-red-600 dark:text-red-400">{errors.content.message}</p>
                )}
                <div className="ml-auto text-xs text-gray-600 dark:text-gray-400">
                  {watch('content')?.length || 0} / 2000
                </div>
              </div>
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-black dark:text-gray-100 mb-2">
                Status
              </label>
              <select
                {...register('status')}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-black dark:text-white"
              >
                <option value="draft">Draft</option>
                <option value="scheduled">Scheduled</option>
                <option value="published">Published</option>
              </select>
            </div>

            {/* Scheduled Date/Time */}
            {watchedStatus === 'scheduled' && (
              <div>
                <label className="block text-sm font-medium text-black dark:text-gray-100 mb-2">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Schedule Date & Time
                </label>
                <input
                  {...register('scheduled_at', {
                    required: watchedStatus === 'scheduled' ? 'Schedule date is required' : false
                  })}
                  type="datetime-local"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-black dark:text-white"
                />
                {errors.scheduled_at && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.scheduled_at.message}</p>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-750">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {isDirty ? 'You have unsaved changes' : 'No changes made'}
            </div>
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={onClose}
                disabled={isLoading}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-black dark:text-gray-100 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 rounded-md transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading || !isDirty}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors disabled:opacity-50 flex items-center"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PostEditModal;